import React from 'react';
import {StyleSheet, Text, View} from 'react-native';

import config from '../../config';
import {radius, space, universal} from '../../design-system/tokens';

import AccountArrowBlack from './assets/account-banner-arrow-black.svg';
import AccountArrowSns from './assets/account-banner-arrow-sns.svg';
import AccountArrowWhite from './assets/account-banner-arrow-white.svg';
import AccountLogoBm from './assets/account-banner-logo-bm.svg';
import AccountLogoHbFrame from './assets/account-banner-logo-hb-frame.svg';
import AccountLogoHbHarrys from './assets/account-banner-logo-hb-harrys.svg';
import AccountLogoHbPerks from './assets/account-banner-logo-hb-perks.svg';
import AccountLogoHbTick from './assets/account-banner-logo-hb-tick.svg';
import AccountLogoSns from './assets/account-banner-logo-sns.svg';
import AccountLogoTcCamel from './assets/account-banner-logo-tc-camel.svg';
import AccountLogoTcRoundel from './assets/account-banner-logo-tc-roundel.svg';
import {chromeType} from './chromeType';
import {menuLinkUnderline} from './MenuLinkText';

/**
 * The per-brand account dropdown banner: accountdropdown-banner 475:23999, 628 × 192.
 * Variants: bottlemart 475:23861, sipnsave 475:24000, thirstycamel 475:24012,
 * harrysperks 475:24022. Artwork is the Figma SVG export; this file only places it.
 * Ported from the header prototype (ea875fae6 MemberBanners.tsx), account banner only.
 */

type Svg = typeof AccountLogoBm;
type Rect = {left: number; top: number; width: number; height: number};
/** `viewBox` repeats the file's own: the web svgr config strips it (SVGO removeViewBox). */
type Layer = Rect & {Component: Svg; viewBox: string};
type Brand = 'BM' | 'SNS' | 'TC' | 'HB';

const brand: Brand = (['BM', 'SNS', 'TC', 'HB'] as const).includes(config.BASE_TENANT as Brand)
  ? (config.BASE_TENANT as Brand)
  : 'BM';

const rect = (left: number, top: number, width: number, height: number): Rect => ({
  left,
  top,
  width,
  height,
});
/** Figma's percentage insets (top, right, bottom, left) inside `box`. */
const inset = (box: Rect, t: number, r: number, b: number, l: number): Rect =>
  rect(
    box.left + (box.width * l) / 100,
    box.top + (box.height * t) / 100,
    (box.width * (100 - l - r)) / 100,
    (box.height * (100 - t - b)) / 100,
  );
const layer = (Component: Svg, at: Rect, viewBox: string): Layer => ({Component, viewBox, ...at});

/** Absolutely placed SVG layers, in design units. */
const Layers = ({layers}: {layers: Layer[]}) => (
  <>
    {layers.map(({Component, viewBox, left, top, width, height}, i) => (
      <View key={i} pointerEvents="none" style={[styles.layer, {left, top}]}>
        <Component viewBox={viewBox} width={width} height={height} aria-hidden={true} />
      </View>
    ))}
  </>
);

export const ACCOUNT_BANNER_WIDTH = 628;
export const ACCOUNT_BANNER_HEIGHT = 192;
// SHORTCUT: Figma layout literals with no token (text column 239.571, arrow 36). Upgrade
// path: layout tokens once DIS-1735 adds them.
const ACCOUNT_TEXT_WIDTH = 239.571;
const ACCOUNT_ARROW_SIZE = 36;
const ARROW_VIEWBOX = `0 0 ${ACCOUNT_ARROW_SIZE} ${ACCOUNT_ARROW_SIZE}`;

const bmSnsLogo = rect(0, 0, 189.381, 140);
const tcAccountLogo = rect(0, 0, 140, 140);
const hbAccountLogo = rect(0, 0, 212.23, 130);

type AccountArt = {
  background: string;
  text: string;
  /** Colour of "Member Offers"; Bottlemart alone draws it in a second colour. */
  emphasis: string;
  logo: Rect & {layers: Layer[]};
  Arrow: Svg;
};

// SHORTCUT: brand hex literals from the Figma variant fills; colour has no token layer
// (design-system-guard). Upgrade path: brand theme colour tokens (DIS-1735).
const ACCOUNT_ART: Record<Brand, AccountArt> = {
  BM: {
    background: '#0054a6',
    text: universal.white,
    emphasis: '#f4d300',
    logo: {...bmSnsLogo, layers: [layer(AccountLogoBm, bmSnsLogo, '0 0 189.381 140')]},
    Arrow: AccountArrowWhite,
  },
  SNS: {
    background: '#ffde00',
    text: '#0054a6',
    emphasis: '#0054a6',
    logo: {...bmSnsLogo, layers: [layer(AccountLogoSns, bmSnsLogo, '0 0 189.381 140')]},
    Arrow: AccountArrowSns,
  },
  TC: {
    background: '#acca57',
    text: universal.black,
    emphasis: universal.black,
    logo: {
      ...tcAccountLogo,
      layers: [
        layer(
          AccountLogoTcRoundel,
          inset(tcAccountLogo, 2.43, 2.15, 1.04, 1.31),
          '0 0 135.15 135.15',
        ),
        layer(
          AccountLogoTcCamel,
          inset(tcAccountLogo, 7.7, 7.37, 53.69, 6.98),
          '0 0 119.912 54.0428',
        ),
      ],
    },
    Arrow: AccountArrowBlack,
  },
  HB: {
    background: universal.black,
    text: universal.white,
    emphasis: universal.white,
    logo: {
      ...hbAccountLogo,
      layers: [
        layer(AccountLogoHbFrame, hbAccountLogo, '0 0 212.23 130'),
        layer(
          AccountLogoHbPerks,
          inset(hbAccountLogo, 49.23, 10.82, 19.21, 31.79),
          '0 0 121.795 41.0238',
        ),
        layer(
          AccountLogoHbHarrys,
          inset(hbAccountLogo, 19.5, 20.96, 48.81, 13.14),
          '0 0 139.846 41.2062',
        ),
        layer(
          AccountLogoHbTick,
          inset(hbAccountLogo, 49.23, 69.64, 18.79, 11.08),
          '0 0 40.9197 41.5709',
        ),
      ],
    },
    // Figma 475:24022 reuses the white Bottlemart arrow.
    Arrow: AccountArrowWhite,
  },
};

/**
 * 628 × 192: headline, programme logo, arrow; space-between, padding 32 / 8. Not pressable:
 * the banner target (Rewards, redline annotation) is wired by Task A10.
 */
export const AccountBanner = ({label, underlined}: {label: string; underlined?: boolean}) => {
  const art = ACCOUNT_ART[brand];
  const {Arrow} = art;
  return (
    <View
      style={[styles.account, {backgroundColor: art.background}]}
      accessible={true}
      accessibilityRole="image"
      accessibilityLabel={label}>
      <Text style={[styles.accountText, {color: art.text}, underlined && menuLinkUnderline]}>
        {'Personalised '}
        <Text style={{color: art.emphasis}}>Member Offers</Text>
      </Text>
      <View style={{width: art.logo.width, height: art.logo.height}}>
        <Layers layers={art.logo.layers} />
      </View>
      <View style={styles.arrow}>
        <Arrow
          viewBox={ARROW_VIEWBOX}
          width={ACCOUNT_ARROW_SIZE}
          height={ACCOUNT_ARROW_SIZE}
          aria-hidden={true}
        />
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  layer: {position: 'absolute'},
  account: {
    width: ACCOUNT_BANNER_WIDTH,
    height: ACCOUNT_BANNER_HEIGHT,
    paddingHorizontal: space.s32,
    paddingVertical: space.s8,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderRadius: radius.card,
    overflow: 'hidden',
  },
  accountText: {
    ...chromeType.banner26Bold,
    width: ACCOUNT_TEXT_WIDTH,
    textTransform: 'uppercase',
  },
  arrow: {transform: [{rotate: '180deg'}]},
});
